import React, { useCallback, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styles from '../../Products.module.scss'

import { SettingsAdjust as SettingsIcon } from '../../../../icons'
import CustomSelect from 'components/elements/inputs/CustomSelect'
import InputGroup from 'components/elements/forms/InputGroup'
import CustomInput from 'components/elements/inputs/CustomInput'
import TabsList from 'components/elements/tabs/TabsList'
import CustomCheckbox from 'components/elements/inputs/CustomCheckbox'
import Flag from 'components/elements/other/Flag'
import Loader from 'components/elements/progress/Loader'
import Accordion from 'components/elements/blocks/Accordion'
import UnblockerManualBlock from '../UnblockerManualBlock'
import UnblockerResult from '../UnblockerResult'
import { ISelectOption } from 'components/elements/inputs/CustomSelect/CustomSelect'

import {
  selectLocations,
  selectLocationsFetching,
} from 'store/service/selectors'
// import { selectUserSubscriptionView } from 'store/user/selectors'
// import { isSubscriptionActive } from 'utils/functions'
import { ServiceActionsTypes } from 'store/service/actions'
import {
  UnblockerProxyTypes,
  UnblockerOutputOptionsDescription
} from 'utils/unblocker/unblockerConstants'

const UnblockerBuilder: React.FC = () => {
  const dispatch = useDispatch()

  // const subscriptionInfo = useSelector(selectUserSubscriptionView)
  const availableCountries = useSelector(selectLocations)
  const countriesFetching = useSelector(selectLocationsFetching)

  // const isUserSubscriptionActive = isSubscriptionActive(subscriptionInfo)
  // const isDemoModeAvailable = !subscriptionInfo?.status

  const [websiteUrl, setWebsiteUrl] = useState('https://example.com')
  const [selectedCountry, setCountry] = useState<ISelectOption>({
    label: 'United States',
    value: 'us',
    icon: <Flag countryCode={'us'} className={styles.optionFlag} />,
  })
  const [selectedProxyTypeIndex, setProxyTypeIndex] = useState(0)
  const [customProxyValue, setCustomProxyValue] = useState('')
  const [isMarkdownParamActive, toggleMarkdownParam] = useState(true)
  const [isBodyParamActive, toggleBodyParam] = useState(false)
  const [isXhrParamActive, toggleXhrParam] = useState(false)
  const [isScreenshotParamActive, toggleScreenshotParam] = useState(false)
  const [isCookiesParamActive, toggleCookiesParam] = useState(false)

  const selectedProxyType = UnblockerProxyTypes[selectedProxyTypeIndex]

  const handleWebsiteUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWebsiteUrl(e.target.value)
  }

  const handleProxyTypeChange = (index: number) => {
    if (index !== selectedProxyTypeIndex) {
      setProxyTypeIndex(index)
      setCountry({
        label: 'United States',
        value: 'us',
        icon: <Flag countryCode={'us'} className={styles.optionFlag} />,
      })

      if (UnblockerProxyTypes[index].value !== 'custom') {
        dispatch({
          type: ServiceActionsTypes.SERVICE_GET_LOCATIONS,
          connectionType: UnblockerProxyTypes[index].value,
        })
      }
    }
  }

  const handleCountryChange = (option: any) => {
    setCountry(option)
  }

  const handleCustomProxyValueChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCustomProxyValue(e.target.value)
  }

  const getCountriesOptions = useCallback(() => {
    let options: ISelectOption[] = []

    if (availableCountries) {
      for (const [code, data] of Object.entries(availableCountries)) {
        options.push({
          // @ts-ignore
          label: data.name,
          value: code,
        })
      }
    }

    options.sort(function (a, b) {
      if (a.label < b.label) {
        return -1
      }
      if (a.label > b.label) {
        return 1
      }
      return 0
    })

    // @ts-ignore
    options.unshift({
      label: 'Most popular',
      options: [
        {
          label: 'United States',
          value: 'us',
          icon: <Flag countryCode={'us'} className={styles.optionFlag} />,
        },
        {
          label: 'United Kingdom',
          value: 'gb',
          icon: <Flag countryCode={'gb'} className={styles.optionFlag} />,
        },
      ],
    })

    return options
  }, [availableCountries])

  const countriesOptions = useMemo(
    () => getCountriesOptions(),
    [getCountriesOptions]
  )

  const iconedCountriesOptions = countriesOptions.map((item) => ({
    ...item,
    icon:
      item.value === 'any' ? null : (
        <Flag countryCode={item.value} className={styles.optionFlag} />
      ),
  }))

  const renderProxyInput = () => {
    if (selectedProxyType.value === 'custom') {
      return (
        <CustomInput
          value={customProxyValue}
          placeholder={'http:// username : password @ server : port'}
          onChange={handleCustomProxyValueChange}
        />
      )
    } else if (countriesFetching) {
      return (
        <div className={styles.countriesLoaderContainer}>
          <Loader />
        </div>
      )
    } else {
      return (
        <CustomSelect
          onChange={handleCountryChange}
          value={selectedCountry}
          placeholder={'start type'}
          isSearchable={true}
          name={'unblocker_countries_select'}
          options={iconedCountriesOptions}
        />
      )
    }
  }

  return (
    <div className={styles.container}>
      <div
        className={`
          ${styles.contentBlock}
          ${styles.leftSide}
        `}
      >
        <div className={styles.blockHeader}>
          <div className={styles.headerIconWrapper}>
            <SettingsIcon />
          </div>
          <span className={styles.blockTitle}>Web Data API config</span>
        </div>

        <div className={styles.form}>
          <InputGroup
            labelTop={'Website URL'}
            name={'unblocker_website_url_input'}
            boldLabel
            description={
              'Retrieves the full HTML source code from the provided web address. This operation attempts to bypass common blocking techniques to ensure successful content fetching.'
            }
            className={styles.groupBlock}
          >
            <CustomInput
              value={websiteUrl}
              placeholder={'https://example.com'}
              onChange={handleWebsiteUrlChange}
            />
          </InputGroup>

          <div className={styles.groupBlock}>
            <InputGroup
              labelTop={'Proxy settings'}
              name={'unblocker_proxy_settings_input'}
              boldLabel
              description={
                'Set the location and device type from which the request will be sent'
              }
            />

            <TabsList
              items={UnblockerProxyTypes}
              activeTabIndex={selectedProxyTypeIndex}
              size={'m'}
              type={'bordered'}
              className={styles.unblockerProxyTypesTabs}
              itemClassName={styles.unblockerProxyTypeItem}
              bold={false}
              onChange={handleProxyTypeChange}
            />

            {renderProxyInput()}
          </div>

          <InputGroup
            labelTop={'Output options'}
            name={'unblocker_output_options_input'}
            boldLabel
            description={UnblockerOutputOptionsDescription}
            className={styles.groupBlock}
          >
            <div className={styles.outputOptionsList}>
              <CustomCheckbox
                className={styles.outputOptionItem}
                id={`unblocker_output_options_item-markdown`}
                checked={isMarkdownParamActive}
                onChange={() => toggleMarkdownParam(!isMarkdownParamActive)}
              >
                Markdown
              </CustomCheckbox>
              <CustomCheckbox
                className={styles.outputOptionItem}
                id={`unblocker_output_options_item-body`}
                checked={isBodyParamActive}
                onChange={() => toggleBodyParam(!isBodyParamActive)}
              >
                HTML content
              </CustomCheckbox>
              <CustomCheckbox
                className={styles.outputOptionItem}
                id={`unblocker_output_options_item-xhr`}
                checked={isXhrParamActive}
                onChange={() => toggleXhrParam(!isXhrParamActive)}
              >
                XHR content
              </CustomCheckbox>
              <CustomCheckbox
                className={styles.outputOptionItem}
                id={`unblocker_output_options_item-screenshot`}
                checked={isScreenshotParamActive}
                onChange={() => toggleScreenshotParam(!isScreenshotParamActive)}
              >
                Screenshot
              </CustomCheckbox>
              <CustomCheckbox
                className={styles.outputOptionItem}
                id={`unblocker_output_options_item-cookies`}
                checked={isCookiesParamActive}
                onChange={() => toggleCookiesParam(!isCookiesParamActive)}
              >
                Cookies
              </CustomCheckbox>
            </div>
          </InputGroup>
        </div>

        <Accordion
          title={'Advanced options'}
          className={styles.optionsAccordion}
          accordionContentClassName={styles.optionsAccordionContent}
        >
          <UnblockerManualBlock />
        </Accordion>
      </div>
      <div
        className={`
          ${styles.contentBlock}
          ${styles.rightSide}
        `}
      >
        <UnblockerResult
          selectedCountry={selectedCountry.value}
          websiteUrl={websiteUrl}
          customProxy={customProxyValue}
          selectedProxyType={UnblockerProxyTypes[selectedProxyTypeIndex].value}
          isMarkdownParamActive={isMarkdownParamActive}
          isBodyParamActive={isBodyParamActive}
          isXhrParamActive={isXhrParamActive}
          isScreenshotParamActive={isScreenshotParamActive}
          isCookiesParamActive={isCookiesParamActive}
        />
      </div>
    </div>
  )
}

export default UnblockerBuilder
